import { Component, inject, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Location } from "@angular/common";
import { ENVIRONMENT } from "@company-box/models";
import { AlertService } from "@company-box/services";
import { AlertComponent } from "ngx-bootstrap/alert";
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from "@angular/router";
import { filter } from "rxjs";
import { BsModalService } from "ngx-bootstrap/modal";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [RouterModule, AlertComponent],
})
export class AppComponent implements OnInit {
  environment = inject(ENVIRONMENT);
  location: Location = inject(Location);
  private titleService: Title = inject(Title);
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private bsModalService: BsModalService = inject(BsModalService);
  alertService = inject(AlertService);

  private title = this.environment.config.title;

  constructor() {}

  ngOnInit(): void {
    this.titleService.setTitle(this.title);

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      let route = this.activatedRoute;
      while (route.firstChild) {
        route = route.firstChild;
      }
      const pageTitle = route.snapshot.data["title"];
      this.titleService.setTitle(pageTitle ? `${pageTitle} | ${this.title}` : this.title);
    });

    // ngx-bootstrap's ModalContainerComponent always renders its inner .modal-dialog with a
    // hard-coded role="document" -- redundant with (and sometimes conflicting with) the
    // role="dialog" + aria-modal="true" already set correctly on the modal-container host.
    // There's no supported way to override this in the library's own config, so strip it here.
    this.bsModalService.onShown.subscribe(() => {
      document.querySelectorAll('.modal-dialog[role="document"]').forEach((el) => el.removeAttribute("role"));
    });

    // Input-modality detection: :focus-visible alone still shows the focus ring on a plain
    // mouse click for text inputs (browsers do this deliberately). Toggling this class lets
    // _focus-visible.scss suppress the ring specifically for mouse interaction while keeping it
    // for keyboard nav. Only Tab (the key that actually moves focus between fields) switches
    // back to "keyboard mode" -- resetting on every keydown would bring the ring back the
    // moment you started typing into a field you'd clicked into, which isn't navigation.
    document.addEventListener("mousedown", () => document.body.classList.add("using-mouse"));
    document.addEventListener("keydown", (event) => {
      if (event.key === "Tab") {
        document.body.classList.remove("using-mouse");
      }
    });

    if (this.environment.production) {
      if (location.protocol === "http:") {
        window.location.href = location.href.replace("http", "https");
      }
      // Dynamic import keeps logrocket (~60 KB) out of the initial bundle - it is only used in production.
      import("logrocket").then((m) => m.default.init("edplti/companybox"));
    }

    if (this.environment.features?.rewards) {
      this.loadLoyaltyLion();
    }

    if (this.environment.features?.hubspotChat) {
      this.loadHubspotChat();
    }
  }

  private loadLoyaltyLion(): void {
    const loader = document.createElement("script");
    loader.text =
      '!function(t,n){function o(n){var o=t.getElementsByTagName("script")[0],i=t.createElement("script");i.src=n,i.crossOrigin="",o.parentNode.insertBefore(i,o)}if(!n.isLoyaltyLion){window.loyaltylion=n,void 0===window.lion&&(window.lion=n),n.version=2,n.isLoyaltyLion=!0;var i=new Date,e=i.getFullYear().toString()+i.getMonth().toString()+i.getDate().toString();o("https://sdk.loyaltylion.net/static/2/loader.js?t="+e);var r=!1;n.init=function(t){if(r)throw new Error("Cannot call loyaltylion.init more than once");r=!0;var a=n._token=t.token;if(!a)throw new Error("Token must be supplied to loyaltylion.init");for(var l=[],s="_push configure bootstrap shutdown on removeListener authenticateCustomer".split(" "),c=0;c<s.length;c+=1)!function(t,n){t[n]=function(){l.push([n,Array.prototype.slice.call(arguments,0)])}}(n,s[c]);o("https://sdk.loyaltylion.net/sdk/start/"+a+".js?t="+e+i.getHours().toString()),n._initData=t,n._buffer=l}}(document,window.loyaltylion||[]);';
    document.head.appendChild(loader);
    (window as any).loyaltylion?.init({token: "59decb16fe0a1ee1f989c7a5c94073ba"});
  }

  private loadHubspotChat(): void {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "hs-script-loader";
    script.async = true;
    script.defer = true;
    script.src = "//js.hs-scripts.com/5828752.js";
    document.body.appendChild(script);
  }
}
